import { FC } from "react";

import { DonationFormCategory } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { useTranslate } from "../../../../../../common/hooks/useTranslate";
import { useLiveFormEditorContext } from "../../../common/LiveFormEditorContext";
import { useBuildPrefilledDescription } from "../../../common/prefillDataBuilders/useBuildPrefilledDescription";
import { useUpdateDonationDataInPreview } from "../../../common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";
import { useFormSaveAndNavigate } from "../../useFormSaveAndNavigate";

import { LiveEditorStepDescriptionStateless } from "./LiveEditorStepDescription.stateless";

import { trpc } from "@/common/helpers/trpc";
import { StepDescriptionFormData } from "@/features/LiveFormEditor/Steps/StepDescription/shared/types";

export const DonationLiveEditorStepDescription: FC = () => {
  const { apiFormObject: apiDonationForm, optionalFundraiserToken } = useLiveFormEditorContext<"donation-form">();
  const { t } = useTranslate();

  const localizedDonationFormField = apiDonationForm.donationFormFields?.find(
    (field) => field.locale === apiDonationForm.locale
  );
  const initialDescription = localizedDonationFormField?.description || null;
  const defaultValues: StepDescriptionFormData = {
    description: initialDescription,
  };

  const form = useForm<StepDescriptionFormData>({ defaultValues });

  const { buildPrefilledDescription } = useBuildPrefilledDescription();
  const prefilledText = buildPrefilledDescription({
    organizationName: apiDonationForm.organization?.name || "",
    isPeerToPeerCampaign: false,
  });

  const { mutateAsync: updateDescription } = trpc.updateDonationFormDescription.useMutation();

  const saveData = async (formData: StepDescriptionFormData) => {
    return updateDescription({
      donationFormId: apiDonationForm.id,
      description: formData.description,
      fundraiserToken: optionalFundraiserToken,
    });
  };
  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepDescriptionFormData>({
    type: "donation-form",
    form,
    saveData,
  });

  useUpdateDonationDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      const descriptionInPreview = formData.description ?? prefilledText;
      liveDataToMutate.donation.donationFormFields![0].description = descriptionInPreview;
      liveDataToMutate.donation.donationFormFields![0].sanitizedDescription = descriptionInPreview;
      liveDataToMutate.placeholders.isDescription = !formData.description;
    },
  });

  const stepTitle =
    apiDonationForm.category === DonationFormCategory.PeerToPeerCampaign
      ? t("dashboard", "liveFormEditor.stepDescription.p2p.title")
      : t("dashboard", "liveFormEditor.stepDescription.donationForm.title");

  return (
    <LiveEditorStepDescriptionStateless
      form={form}
      handleSaveAndNext={handleSaveAndNext}
      handleSaveAndBack={handleSaveAndBack}
      initialDescription={initialDescription}
      prefilledText={prefilledText}
      wording={{
        stepTitle,
        stepDescription: t("dashboard", "liveFormEditor.stepDescription.donationForm.description"),
      }}
    />
  );
};
